import React, { useState, useMemo } from 'react';
import {
    AreaChart, Area, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

/**
 * Exponential decay: y = start * e^(-λt)
 * With CI ribbons: upper = y * 1.1, lower = y * 0.9 (±10% band)
 */
function computeDecay(lambda, startRate, horizonHours) {
    const points = [];
    const step = horizonHours <= 24 ? 1 : horizonHours <= 72 ? 3 : 6;

    for (let h = 0; h <= horizonHours; h += step) {
        const t = h / 24; // convert to days
        const y = startRate * Math.exp(-lambda * t);
        const upper = Math.min(100, y * 1.1);
        const lower = Math.max(0, y * 0.9);

        points.push({
            hour: h,
            label: horizonHours <= 24 ? `${h}h` : `${(h / 24).toFixed(1)}d`,
            value: Number(y.toFixed(2)),
            upper: Number(upper.toFixed(2)),
            lower: Number(lower.toFixed(2)),
            ciRange: [Number(lower.toFixed(2)), Number(upper.toFixed(2))]
        });
    }
    return points;
}

const HORIZONS = [
    { label: '24h', value: 24 },
    { label: '72h', value: 72 },
    { label: '7 days', value: 168 },
];

export default function DecaySimulator({ currentRate = 50 }) {
    const [lambda, setLambda] = useState(0.15);
    const [startRate, setStartRate] = useState(currentRate);
    const [horizon, setHorizon] = useState(168);

    const data = useMemo(
        () => computeDecay(lambda, startRate, horizon),
        [lambda, startRate, horizon]
    );

    const finalValue = data.length > 0 ? data[data.length - 1].value : 0;

    return (
        <div className="card sim-fade-card">
            <h2>🌊 Sim Fade — Interactive Decay</h2>
            <p className="sim-subtitle">
                Model what happens if no new mentions occur. Drag the sliders to explore.
            </p>

            {/* Controls */}
            <div className="sim-controls">
                <div className="sim-slider-group">
                    <label className="sim-label">
                        <span>λ (decay rate)</span>
                        <span className="sim-value">{lambda.toFixed(2)}</span>
                    </label>
                    <input
                        type="range"
                        min="0.05"
                        max="0.3"
                        step="0.01"
                        value={lambda}
                        onChange={e => setLambda(Number(e.target.value))}
                        className="sim-slider"
                    />
                    <div className="sim-range-labels">
                        <span>Slow</span><span>Fast</span>
                    </div>
                </div>

                <div className="sim-slider-group">
                    <label className="sim-label">
                        <span>Initial Rate (%)</span>
                        <span className="sim-value">{startRate}%</span>
                    </label>
                    <input
                        type="range"
                        min="1"
                        max="100"
                        step="1"
                        value={startRate}
                        onChange={e => setStartRate(Number(e.target.value))}
                        className="sim-slider"
                    />
                    <div className="sim-range-labels">
                        <span>1%</span><span>100%</span>
                    </div>
                </div>

                <div className="sim-slider-group">
                    <label className="sim-label">
                        <span>Horizon</span>
                    </label>
                    <div className="sim-horizon-buttons">
                        {HORIZONS.map(h => (
                            <button
                                key={h.value}
                                className={`sim-horizon-btn ${horizon === h.value ? 'active' : ''}`}
                                onClick={() => setHorizon(h.value)}
                            >
                                {h.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Chart */}
            <div className="sim-chart-container">
                <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="decayGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#667eea" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#764ba2" stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="ciGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#667eea" stopOpacity={0.15} />
                                <stop offset="95%" stopColor="#764ba2" stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 12, fill: '#666' }}
                            interval={Math.max(0, Math.floor(data.length / 8) - 1)}
                        />
                        <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 12, fill: '#666' }}
                            tickFormatter={v => `${v}%`}
                        />
                        <Tooltip
                            contentStyle={{
                                background: 'rgba(255,255,255,0.95)',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                            formatter={(value, name) => {
                                if (name === 'ciRange') return null;
                                return [`${value}%`, name === 'value' ? 'Mention Rate' : name];
                            }}
                        />
                        {/* CI ribbon */}
                        <Area
                            type="monotone"
                            dataKey="ciRange"
                            fill="url(#ciGradient)"
                            stroke="none"
                            animationDuration={500}
                            animationEasing="ease-in-out"
                            isAnimationActive={true}
                        />
                        {/* Main decay line */}
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#667eea"
                            strokeWidth={3}
                            fill="url(#decayGradient)"
                            animationDuration={500}
                            animationEasing="ease-in-out"
                            isAnimationActive={true}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* Summary */}
            <div className="sim-summary">
                <div className="sim-summary-item">
                    <span className="sim-summary-label">Start</span>
                    <span className="sim-summary-value">{startRate}%</span>
                </div>
                <div className="sim-summary-arrow">→</div>
                <div className="sim-summary-item">
                    <span className="sim-summary-label">End</span>
                    <span className={`sim-summary-value ${finalValue < startRate * 0.3 ? 'critical' : ''}`}>
                        {finalValue}%
                    </span>
                </div>
                <div className="sim-summary-item">
                    <span className="sim-summary-label">Drop</span>
                    <span className="sim-summary-value negative">
                        -{(startRate - finalValue).toFixed(1)}%
                    </span>
                </div>
            </div>

            <p className="sim-footnote">
                Transparency, not threat — this shows the natural decay curve if no new seeding occurs.
            </p>
        </div>
    );
}
